/**
 * SCRUM-128 / SCRUM-194 — pantalla del cliente (tablet).
 * /display/:token — poll 1.5s del estado publicado por la caja.
 * Branding white-label desde SoT (nombre, logo, colores del tenant).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type CSSProperties } from "react";

type DisplayBranding = {
  businessName?: string | null;
  logoUrl?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  backgroundColor?: string | null;
};

type DisplayState = {
  phase?: string;
  branding?: DisplayBranding;
  cart?: {
    lines: Array<{
      name: string;
      qty: number;
      unitPriceCents: number;
      lineTotalCents: number;
    }>;
    totalCents: number;
    currency: string;
  } | null;
  payment?: {
    providerId: string;
    method: string;
    status: string;
    amountCents: number;
    currency: string;
    instruction?: string | null;
    qr?: { svg?: string | null; payload?: string | null } | null;
  } | null;
  result?: { status: string; message?: string | null } | null;
  updatedAt?: string;
};

const pesos = (cents: number, currency = "ARS") =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(cents / 100);

/** Luminancia relativa sRGB (0–1). */
function relativeLuminance(hex: string): number {
  const raw = hex.replace("#", "").trim();
  const full =
    raw.length === 3
      ? raw
          .split("")
          .map((c) => c + c)
          .join("")
      : raw.slice(0, 6);
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return 0.1;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

function isLightBackground(bg: string): boolean {
  return relativeLuminance(bg) > 0.45;
}

export const Route = createFileRoute("/display/$token")({
  component: CustomerDisplayPage,
});

function CustomerDisplayPage() {
  const { token } = Route.useParams();
  const [state, setState] = useState<DisplayState | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`/api/display/${encodeURIComponent(token)}`, {
          credentials: "omit",
        });
        if (!res.ok) {
          if (!cancelled) setError(res.status === 404 ? "Display no vinculado" : "Error al cargar");
          return;
        }
        const data = (await res.json()) as { state?: DisplayState };
        if (!cancelled) {
          setError(null);
          setState(data.state ?? null);
        }
      } catch {
        if (!cancelled) setError("Sin conexión");
      }
    };
    void load();
    const id = window.setInterval(() => void load(), 1500);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [token]);

  const branding = state?.branding;
  const brand = branding?.businessName?.trim() || "Tu comercio";
  const phase = state?.phase ?? "idle";
  const primary = branding?.primaryColor?.trim() || "#16a34a";
  const secondary = branding?.secondaryColor?.trim() || "#15803d";
  const background = branding?.backgroundColor?.trim() || "#0c1220";
  const light = useMemo(() => isLightBackground(background), [background]);
  const fg = light ? "#0f172a" : "#f8fafc";
  const muted = light ? "rgba(15,23,42,0.55)" : "rgba(248,250,252,0.55)";
  const hairline = light ? "rgba(15,23,42,0.12)" : "rgba(248,250,252,0.12)";

  return (
    <div
      className="flex min-h-dvh flex-col"
      style={
        {
          backgroundColor: background,
          color: fg,
          ["--cd-primary" as string]: primary,
          ["--cd-secondary" as string]: secondary,
          ["--cd-bg" as string]: background,
          ["--cd-fg" as string]: fg,
          ["--cd-muted" as string]: muted,
          ["--cd-hairline" as string]: hairline,
        } as CSSProperties
      }
    >
      <header className="border-b px-6 py-4" style={{ borderColor: hairline }}>
        <div className="flex items-center gap-3">
          {branding?.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt=""
              className="max-h-12 w-auto object-contain"
            />
          ) : null}
          <h1 className="text-2xl font-semibold tracking-tight">{brand}</h1>
        </div>
      </header>

      <main className="flex flex-1 flex-col items-center justify-center px-6 py-8">
        {error ? (
          <p className="text-center text-lg" style={{ color: muted }}>
            {error}
          </p>
        ) : phase === "idle" || !state ? (
          <p className="text-center text-xl" style={{ color: muted }}>
            Esperando cobro…
          </p>
        ) : phase === "cart" && state.cart ? (
          <CartView cart={state.cart} hairline={hairline} muted={muted} />
        ) : phase === "payment" && state.payment ? (
          <PaymentView payment={state.payment} cart={state.cart} muted={muted} primary={primary} />
        ) : phase === "result" && state.result ? (
          <ResultView result={state.result} primary={primary} />
        ) : (
          <p className="text-center text-xl" style={{ color: muted }}>
            Esperando cobro…
          </p>
        )}
      </main>
    </div>
  );
}

function CartView({
  cart,
  hairline,
  muted,
}: {
  cart: NonNullable<DisplayState["cart"]>;
  hairline: string;
  muted: string;
}) {
  return (
    <div className="w-full max-w-xl space-y-6">
      <ul className="space-y-3">
        {cart.lines.map((l, i) => (
          <li key={`${l.name}-${i}`} className="flex justify-between gap-4 text-lg">
            <span>
              {l.qty} × {l.name}
            </span>
            <span className="tabular-nums" style={{ color: muted }}>
              {pesos(l.lineTotalCents, cart.currency)}
            </span>
          </li>
        ))}
      </ul>
      <div
        className="flex justify-between border-t pt-4 text-3xl font-semibold"
        style={{ borderColor: hairline }}
      >
        <span>Total</span>
        <span className="tabular-nums">{pesos(cart.totalCents, cart.currency)}</span>
      </div>
    </div>
  );
}

function PaymentView({
  payment,
  cart,
  muted,
  primary,
}: {
  payment: NonNullable<DisplayState["payment"]>;
  cart: DisplayState["cart"];
  muted: string;
  primary: string;
}) {
  return (
    <div className="flex w-full max-w-2xl flex-col items-center gap-6 text-center">
      <p className="text-lg" style={{ color: muted }}>
        {payment.instruction ?? "Escaneá el QR para pagar"}
      </p>
      <p className="text-4xl font-semibold tabular-nums" style={{ color: primary }}>
        {pesos(payment.amountCents, payment.currency)}
      </p>
      {payment.qr?.svg ? (
        <div
          className="rounded-2xl bg-white p-4 shadow-lg"
          dangerouslySetInnerHTML={{ __html: payment.qr.svg }}
        />
      ) : payment.method === "cash" ? (
        <p className="text-2xl" style={{ color: primary }}>
          Pagá en efectivo en caja
        </p>
      ) : payment.method === "card_terminal" ? (
        <p className="text-2xl" style={{ color: primary }}>
          Acercá o insertá tu tarjeta
        </p>
      ) : (
        <p className="text-xl" style={{ color: muted }}>
          Seguí las instrucciones en caja
        </p>
      )}
      {cart && cart.lines.length > 0 ? (
        <p className="text-sm" style={{ color: muted }}>
          {cart.lines.length} ítem(s) en el ticket
        </p>
      ) : null}
      <p className="text-xs uppercase tracking-widest" style={{ color: muted }}>
        Estado: {payment.status}
      </p>
    </div>
  );
}

function ResultView({
  result,
  primary,
}: {
  result: NonNullable<DisplayState["result"]>;
  primary: string;
}) {
  const ok = result.status === "approved";
  return (
    <div className="space-y-4 text-center">
      <p
        className="text-5xl font-semibold"
        style={{ color: ok ? primary : "#f43f5e" }}
      >
        {ok ? "✓" : "✕"}
      </p>
      <p className="text-3xl font-medium">{result.message ?? result.status}</p>
    </div>
  );
}
